import type { ReactNode } from 'react'

import type { Tone } from './StatusBadge'

export interface CalloutProps {
  tone?: Tone
  /** Short heading. Optional — a one-line callout does not need one. */
  title?: string
  children: ReactNode
  /**
   * Announce the message when it appears. Use 'alert' for something the user
   * caused and must act on, 'status' for a result they are waiting for, and
   * leave it unset for text that was already on the page — a live region that
   * fires on every render is noise a screen reader user cannot mute.
   */
  live?: 'alert' | 'status'
  actions?: ReactNode
}

/**
 * Callout — a bordered notice.
 *
 * Distinct from `.lg-alert`, which is danger-toned by construction and used
 * for form-level failures. This one carries a tone, because "your codes are
 * saved", "you have three left", and "this cannot be undone" are three
 * different messages and rendering them identically trains people to skip all
 * three.
 *
 * The tone never carries the meaning on its own: a title or the text itself
 * always says what is going on (WCAG 1.4.1).
 */
export function Callout({ tone = 'info', title, children, live, actions }: CalloutProps) {
  return (
    <div
      className={`lg-callout lg-callout--${tone}`}
      role={live === 'alert' ? 'alert' : live === 'status' ? 'status' : undefined}
      aria-live={live === 'status' ? 'polite' : undefined}
    >
      <div className="lg-callout__body">
        {title && <p className="lg-callout__title">{title}</p>}
        <div className="lg-callout__text">{children}</div>
      </div>
      {actions && <div className="lg-callout__actions">{actions}</div>}
    </div>
  )
}
